import React, { useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  createMoldador,
  deleteMoldador,
  getMoldador,
  updateMoldador,
} from '../services/moldadorService';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import RecordViewModal from '../components/RecordViewModal';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { onlyDigits, formatCpf, formatPhone } from '../utils/formatters';
import useSubmitLock from '../hooks/useSubmitLock';
import { getSessionRole } from '../services/authSession';

const INITIAL_FORM = { nome: '', cpf: '', telefone: '', email: '', ativo: true };

// Normaliza um moldador da API para o formato da tela.
function normalizeMoldador(item) {
  // AJUSTE BACKEND AQUI: chaves aceitas para resposta da API de moldador.
  return {
    id: String(item.id || item.idMoldador || item.id_moldador || `mol-${Date.now()}-${Math.random()}`),
    nome: item.nomeMoldador || item.nome || '',
    cpf: onlyDigits(item.cpf || item.numrCpf || ''),
    telefone: onlyDigits(item.telefone || item.numrTelefone || ''),
    email: item.email || item.enderecoEmail || '',
    registro: item.registro || item.numrRegistro || item.codigo || '',
    ativo: item.ativo !== false,
  };
}

// Tela de Gestão de Moldadores: lista, busca, cadastra, edita e exclui (CRUD; ações restritas a admin).
function MoldadoresPage() {
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [moldadores, setMoldadores] = useState([]);
  const [viewMoldador, setViewMoldador] = useState(null);

  const role = getSessionRole();
  const canManage = role === 'admin';
  const canCreate = role === 'admin' || role === 'consultor' || !role;

  // Carrega os moldadores da API ao montar.
  useEffect(() => {
    let isMounted = true;
    const loadMoldadores = async () => {
      try {
        // AJUSTE BACKEND AQUI: endpoint de listagem de moldadores na tela.
        const data = await getMoldador();
        if (!isMounted) return;
        if (Array.isArray(data)) {
          setMoldadores(data.map(normalizeMoldador));
        }
      } catch (error) {
        if (isMounted) setMoldadores([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadMoldadores();
    return () => { isMounted = false; };
  }, []);

  const filteredMoldadores = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return moldadores;
    return moldadores.filter((item) => (
      item.nome.toLowerCase().includes(normalized) ||
      item.email.toLowerCase().includes(normalized) ||
      item.registro.toLowerCase().includes(normalized)
    ));
  }, [moldadores, query]);

  const stats = useMemo(() => {
    const total = moldadores.length;
    const ativos = moldadores.filter((item) => item.ativo).length;
    return { total, ativos, inativos: total - ativos };
  }, [moldadores]);

  // Handler de onChange por campo (checkbox usa `checked`, demais usam `value`).
  const handleFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handler de onChange que aplica uma máscara (CPF/telefone) antes de salvar.
  const handleMaskedChange = (field, formatter) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: formatter(event.target.value) }));
  };

  // Abre o modal em modo de criação (reseta o formulário).
  const openModal = () => {
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Abre o modal em modo de edição, preenchendo o formulário com máscaras aplicadas.
  const openEditModal = (item) => {
    setEditingId(item.id);
    setFormData({
      nome: item.nome,
      cpf: formatCpf(item.cpf),
      telefone: formatPhone(item.telefone),
      email: item.email,
      ativo: item.ativo,
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Fecha o modal e limpa edição/erros.
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormErrors({});
  };

  // Valida obrigatórios (nome/telefone/email) e formato; retorna erros + telefone sem máscara.
  const validateForm = () => {
    const errors = {};
    if (!formData.nome.trim()) errors.nome = 'Nome obrigatório.';

    const phoneDigits = onlyDigits(formData.telefone);
    if (!phoneDigits) errors.telefone = 'Telefone obrigatório.';
    else if (phoneDigits.length < 10 || phoneDigits.length > 11) errors.telefone = 'Telefone inválido.';

    // Email opcional: valida o formato apenas quando preenchido.
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email.trim())) errors.email = 'Email inválido.';

    return { errors, phoneDigits };
  };

  // Submete o form: valida, checa email duplicado, cria/atualiza na API e refaz a lista.
  const [submitting, handleSave] = useSubmitLock(async (event) => {
    event.preventDefault();
    const { errors } = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const emailTrim = formData.email.trim().toLowerCase();
    const duplicate = emailTrim && moldadores.some((item) => {
      if (editingId && item.id === editingId) return false;
      return (item.email || '').toLowerCase() === emailTrim;
    });

    if (duplicate) {
      setFormErrors({ email: 'Email já cadastrado.' });
      return;
    }

    try {
      if (editingId) {
        await updateMoldador(editingId, formData);
        setMoldadores((prev) =>
          prev.map((item) =>
            item.id === editingId
              ? {
                ...item,
                nome: formData.nome.trim(),
                cpf: onlyDigits(formData.cpf),
                telefone: onlyDigits(formData.telefone),
                email: formData.email.trim(),
                ativo: formData.ativo,
              }
              : item
          )
        );
        showSuccessToast('Moldador atualizado com sucesso.');
      } else {
        // AJUSTE BACKEND AQUI: fluxo de criacao de moldador.
        const { response: created, payload } = await createMoldador(formData);

        const normalizedCreated = created
          ? normalizeMoldador(created)
          : { id: `mol-${Date.now()}`, ...payload };

        setMoldadores((prev) => [normalizedCreated, ...prev]);
        showSuccessToast('Moldador cadastrado com sucesso.');
      }

      closeModal();
    } catch (error) {
      setFormErrors({ geral: error?.message || 'Não foi possível salvar o moldador.' });
    }
  });

  // Exclui o moldador após confirmação e remove da lista local.
  const handleDelete = async (id) => {
    const confirm = window.confirm("Você tem certeza que deseja excluir este moldador?");
    if (!confirm) return;

    try {
      // AJUSTE BACKEND AQUI: exclusao de moldador por id.
      await deleteMoldador(id);
      setMoldadores((prev) => prev.filter((item) => item.id !== id));
      showSuccessToast('Moldador excluído com sucesso.');
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível excluir o moldador.');
    }
  };
  return (
    <div className="moldadores-page space-y-6">
      <section className="moldadores-header flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Gestão de moldadores</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Cadastre os responsaveis pela moldagem para usar nos fluxos de concretagem.
          </p>
        </div>
        {canCreate ? (
          <button
            type="button"
            onClick={openModal}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            Novo moldador
          </button>
        ) : null}
      </section>

      <section className="moldadores-metrics grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Ativos</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.ativos}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Inativos</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.inativos}</p>
        </div>
      </section>

      <section className="moldadores-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            query={query}
            onChange={setQuery}
            placeholder="Buscar por nome ou email"
            resultCount={filteredMoldadores.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Telefone</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span>Ações</span>
                    <ExpandableSearchFilter
                      variant="inline"
                      query={query}
                      onChange={setQuery}
                      placeholder="Buscar..."
                      resultCount={filteredMoldadores.length}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`sk-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: 5 }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredMoldadores.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                    Nenhum moldador encontrado.
                  </td>
                </tr>
              ) : (
                filteredMoldadores.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                    <td className="px-4 py-4 font-medium text-slate-900">{item.nome}</td>
                    <td className="px-4 py-4 text-slate-600">{formatPhone(item.telefone)}</td>
                    <td className="px-4 py-4 text-slate-600">{item.email}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${item.ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}
                      >
                        {item.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setViewMoldador(item)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                        {canManage ? (
                          <button
                            type="button"
                            onClick={() => openEditModal(item)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Editar
                          </button>
                        ) : null}
                        {canManage ? (
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Excluir
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-sk-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredMoldadores.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhum moldador encontrado.
            </div>
          ) : (
            filteredMoldadores.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-base font-semibold text-slate-900">{item.nome}</p>
                    <p className="text-xs text-slate-500">{item.email}</p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium ${item.ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                      }`}
                  >
                    {item.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div className="mt-3 space-y-1 text-xs text-slate-600">
                  <p>Telefone: {formatPhone(item.telefone)}</p>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setViewMoldador(item)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visualizar
                  </button>
                  {canManage ? (
                    <button
                      type="button"
                      onClick={() => openEditModal(item)}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Editar
                    </button>
                  ) : null}
                  {canManage ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Excluir
                    </button>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <RecordViewModal
        open={!!viewMoldador}
        onClose={() => setViewMoldador(null)}
        title="Detalhes do moldador"
        sections={viewMoldador ? [
          {
            title: 'Dados gerais',
            fields: [
              { label: 'Nome', value: viewMoldador.nome || '-' },
              { label: 'CPF', value: formatCpf(viewMoldador.cpf) || '-' },
              { label: 'Telefone', value: formatPhone(viewMoldador.telefone) || '-' },
              { label: 'Email', value: viewMoldador.email || '-' },
              { label: 'Status', value: viewMoldador.ativo ? 'Ativo' : 'Inativo' },
            ],
          },
        ] : []}
      />

      {isModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeModal}
            aria-label="Fechar modal"
          />
          <form
            onSubmit={handleSave}
            className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cadastro</p>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingId ? 'Editar moldador' : 'Cadastrar moldador'}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {formErrors.geral ? (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formErrors.geral}
              </div>
            ) : null}

            <div className="mt-6 space-y-3 sm:space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Nome <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={handleFieldChange('nome')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
                {formErrors.nome ? <p className="text-xs text-rose-600">{formErrors.nome}</p> : null}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Telefone <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  value={formData.telefone}
                  onChange={handleMaskedChange('telefone', formatPhone)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
                {formErrors.telefone ? <p className="text-xs text-rose-600">{formErrors.telefone}</p> : null}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">CPF</label>
                <input
                  type="text"
                  value={formData.cpf}
                  onChange={handleMaskedChange('cpf', formatCpf)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
                {formErrors.cpf ? <p className="text-xs text-rose-600">{formErrors.cpf}</p> : null}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={handleFieldChange('email')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                />
                {formErrors.email ? <p className="text-xs text-rose-600">{formErrors.email}</p> : null}
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={formData.ativo}
                  onChange={handleFieldChange('ativo')}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Moldador ativo
              </label>

              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Salvar moldador'}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default MoldadoresPage;
